'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface RevokeCITokenResult {
  message: string;
  tokenId: string;
}

/**
 * Hook for revoking a CI token (admin only on the server).
 * DELETE /api/v1/organization/ci-tokens/{tokenId}
 */
export function useRevokeCIToken() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<RevokeCITokenResult, Error, string>({
    mutationFn: async (tokenId: string) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.DELETE(
        '/api/v1/organization/ci-tokens/{tokenId}',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            path: {
              tokenId: tokenId,
            },
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to revoke CI token');
      }

      return {
        message: data.message,
        tokenId: data.tokenId,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-tokens'] });
    },
  });
}
